import { useAppSelector } from "@/store/store-hooks";
import { GUID } from "@faro-lotv/foundation";
import { selectCompanyId, selectProjectId } from "@faro-lotv/project-source";
import { useApiClientContext } from "@faro-lotv/service-wires";
import { useEffect } from "react";
import { useWorldMapStore } from "./world-map-store";
import { GeoPosition } from "./world-map-tiles";

function isGeoPosition(value: unknown): value is GeoPosition {
  return (
    typeof value === "object" &&
    value !== null &&
    "lat" in value &&
    "lng" in value &&
    typeof value.lat === "number" &&
    typeof value.lng === "number"
  );
}

/**
 * Reads the project coordinates from the Dashboard project details (not wrapped by @faro-lotv/service-wires yet)
 *
 * @param coreApiUrl Base URL of the CoreApi
 * @param companyId Workspace of the project
 * @param projectId The project to read the coordinates of
 * @param signal To abort the request
 * @returns The project coordinates set in the Dashboard, or null if none are set
 */
async function fetchProjectPosition(
  coreApiUrl: string,
  companyId: GUID,
  projectId: GUID,
  signal: AbortSignal,
): Promise<GeoPosition | null> {
  const response = await fetch(
    `${coreApiUrl}/v3/dashboard/company/${companyId}/project/${projectId}`,
    { credentials: "include", headers: { Accept: "application/json" }, signal },
  );
  if (!response.ok) return null;
  const body: unknown = await response.json();
  const position =
    typeof body === "object" &&
    body !== null &&
    "data" in body &&
    typeof body.data === "object" &&
    body.data !== null &&
    "position" in body.data
      ? body.data.position
      : undefined;
  return isGeoPosition(position) ? position : null;
}

/**
 * Loads the Dashboard coordinates of the current project into the world map store
 *
 * @returns The coordinates, null if the project has none, undefined while loading
 */
export function useProjectGeoPosition(): GeoPosition | null | undefined {
  const projectId = useAppSelector(selectProjectId);
  const companyId = useAppSelector(selectCompanyId);
  const { coreApiClient } = useApiClientContext();
  const { projectPosition, setProjectPosition } = useWorldMapStore();
  const loaded =
    projectId && projectPosition?.projectId === projectId
      ? projectPosition
      : undefined;

  useEffect(() => {
    if (!projectId || !companyId || loaded) return;
    const controller = new AbortController();
    fetchProjectPosition(
      coreApiClient.baseUrl,
      companyId,
      projectId,
      controller.signal,
    )
      .catch(() => null)
      .then((position) => {
        if (!controller.signal.aborted) setProjectPosition(projectId, position);
      });
    return () => controller.abort();
  }, [companyId, coreApiClient, loaded, projectId, setProjectPosition]);

  return loaded?.position;
}
